import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import { Alert, CustomInput, FormText } from 'reactstrap'
import { CurrencyInputField, LoaderButton } from 'components/molecules'
import { ProductCategorySelectDropdownField } from 'domains/product-category/select-dropdown-field'
import ProductCategoryService from 'domains/product-category/services/product-category-service'
import { CATEGORY_OPTIONS } from 'domains/fast-sell-product/constants'
import PriceUtil from 'utils/price-util'
import MarketplaceProductsRevenuesApi from 'api/v2/admins/marketplace/products/marketplace-products-revenues-api'
import fastSellProductConvertToMarketProductFormInitialValues from './fast-sell-product-convert-to-market-product-form-initial-values'
import fastSellProductConvertToMarketProductFormSchema from './fast-sell-product-convert-to-market-product-form-schema'

const SellerRevenueExample = ({ revenues }) => {
  const [ totalDeduction, setTotalDeduction ] = useState(revenues.seller_revenue)
  const [ authFees, setAuthFees ]             = useState(0)

  const authenticationFee = 150000
  useEffect(() => {
    const isSellingPriceBelowRange = revenues.selling_price <= 2500000
    if (isSellingPriceBelowRange) {
      setTotalDeduction(revenues.seller_revenue - authenticationFee)
      setAuthFees(authenticationFee)
    } else {
      setTotalDeduction(revenues.seller_revenue)
      setAuthFees(0)
    }
  }, [ revenues ])

  return (
    <dl style={{ backgroundColor: '#F8F8F8' }} className='row small rounded p-2'>
      <dt className='col-12 pb-2'>{ 'Contoh' }</dt>
      <dt className='col-7 font-weight-normal'>{ 'Harga penawaran Kamu:' }</dt>
      <dd className='col-5 font-weight-normal text-nowrap'>{ PriceUtil.format(revenues.seller_revenue, true, 'IDR') }</dd>

      <dt className='col-7 font-weight-normal'>{ 'Harga jual yang dipublish:' }</dt>
      <dd className='col-5 font-weight-normal text-nowrap'>{ PriceUtil.format(revenues.selling_price, true, 'IDR') }</dd>

      <dt className='col-7 font-weight-normal'>{ 'Komisi Tinkerlust:' }</dt>
      <dd className='col-5 font-weight-normal text-nowrap text-muted'>{ '- ' } { PriceUtil.format(revenues.commission_fee, true, 'IDR') }</dd>

      <dt className='col-7 font-weight-normal'>{ 'Biaya autentikasi:' }</dt>
      <dd className='col-5 font-weight-normal text-nowrap text-muted'>{ `-${PriceUtil.format(authFees, true, 'IDR')}` }</dd>

      <dt className='col-7 font-weight-normal border-top pt-2 font-weight-bold'>{ 'Revenue yang diterima:' }</dt>
      <dd className='col-5 font-weight-normal text-nowrap border-top pt-2 font-weight-bold'>{ PriceUtil.format(totalDeduction, true, 'IDR') }</dd>
    </dl>
  )}

SellerRevenueExample.propTypes = { revenues: PropTypes.object }

const propTypes = {
  fastSellProduct : PropTypes.object,
  onSubmit        : PropTypes.func,
  pageUtils       : PropTypes.object
}

const defaultProps = {
  fastSellProduct : {},
  onSubmit        : () => { },
  pageUtils       : {}
}

const FastSellProductConvertToMarketProductForm = ({
  fastSellProduct,
  onSubmit,
  pageUtils
}) => {

  const [ isAgreementChecked, setIsAgreementChecked ] = useState(false)
  const [ categoryOptions, setCategoryOptions ]       = useState([])
  const [ revenues, setRevenues ]                     = useState({})

  let userSearchTimeout = null

  useEffect(() => {
    const groupOptions = ProductCategoryService.formatToSelectOptions(CATEGORY_OPTIONS)
    setCategoryOptions(groupOptions)
  }, [])

  const getRevenues = (inputPrice) => {
    const minimumPrice        = 60000
    const isUnderMinimumPrice = inputPrice < minimumPrice

    if (isUnderMinimumPrice) return

    MarketplaceProductsRevenuesApi.get({
      params: {
        price : inputPrice,
        plan  : 'mark_up'
      }
    })
      .then((response) => setRevenues(response.data.revenues))
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  // Getting revenue information to be displayed in TnC
  useEffect(() => {
    const price = fastSellProduct?.price_offers[ 0 ]?.price
    getRevenues(price)
  }, [ fastSellProduct ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePriceChange = (inputPrice) => {
    const price = inputPrice
    if (userSearchTimeout) clearTimeout(userSearchTimeout)
    userSearchTimeout = setTimeout(() => {
      getRevenues(price)
    }, 700)
  }

  const isRenderAuthFeesWarning = revenues.selling_price < 2500000

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={ fastSellProductConvertToMarketProductFormInitialValues(fastSellProduct) }
      validationSchema={ fastSellProductConvertToMarketProductFormSchema }
      onSubmit={ onSubmit }
    >
      {
        (formik) => (
          <Form>
            <div className='shadow-sm rounded mb-3 p-3'>
              <div className='d-flex'>
                <div style={{ maxWidth: '100px' }}>
                  <img
                    src={ fastSellProduct?.images[ 0 ]?.s3_url }
                    className='w-100'
                  />
                </div>
                <div className='pl-5'>
                  <div className='font-weight-bold'>{ fastSellProduct.product_brand.label }</div>
                  <div  className='pb-3'>{ fastSellProduct.name }</div>

                  <small>{ 'Tawaran Pertama:' }</small>
                  <div style={{ color: 'brown' }}>
                    { PriceUtil.formatToIDR(fastSellProduct?.price_offers[ 0 ]?.price) }
                  </div>
                </div>
              </div>

              <ProductCategorySelectDropdownField
                label={ 'Kategori' }
                name={ 'product_category' }
                formtext={ 'Pilih kategori yang sesuai.' }
                options={ categoryOptions }
                required={ true }
              />

              <CurrencyInputField
                name={ 'price_offer' }
                label={ 'Harga yang ditawarkan' }
                onKeyUp={ () => handlePriceChange(formik.values.price_offer) }
                required={ true }
              />
              <FormText>
                { 'Harga yang dipublish : ' }
                { PriceUtil.format(revenues?.selling_price, true, 'IDR') }
              </FormText>
            </div>

            <ol className='pl-3'>
              <li value='1' className='pl-2'>
                { 'Harga yang akan dipublish di aplikasi/website Tinkerlust sudah termasuk komisi Tinkerlust dan biaya autentikasi.' }
              </li>
            </ol>
            <SellerRevenueExample revenues={ revenues }/>
            {
              isRenderAuthFeesWarning && (
                <Alert color='info' className='small'>
                  <i className='fas fa-info-circle mr-1' />
                  { 'Untuk produk luxury yang terjual dengan harga ≤ IDR 2.500.000 maka revenue kamu akan dikurangi biaya pemeriksaan jasa authenticator sebesar IDR 150,000.' }
                </Alert>
              )
            }

            <ol className='pl-3'>
              <li value='2' className='pl-2'>{ 'Untuk produk luxury yang terjual dengan harga ≤ IDR 2.500.000 maka revenue kamu akan dikurangi biaya pemeriksaan jasa authenticator sebesar IDR 250,000' }</li>
              <li value='3' className='pl-2'>{ 'Jika diperlukan informasi tambahan mengenai detail produk, penjual bersedia memberikan informasi yang diminta.' }</li>
              <li value='4' className='pl-2'>{ 'Syarat dan ketentuan metode penjualan Marketplace di bawah ini berlaku untuk setiap produk yang dijual menggunakan metode tersebut.' }    </li>
            </ol>

            <CustomInput
              id={ 'confirmation-convert-agreement' }
              color='secondary'
              type='checkbox'
              name='confirmation-convert-agreement'
              label={
                <p>
                  { 'Saya setuju dengan' }
                  <a href='https://www.tinkerlust.com/sell/marketplace/terms-and-conditions'>
                    { ' syarat & ketentuan ' }
                  </a>{ 'yang berlaku' }
                </p>
              }
              checked={ isAgreementChecked }
              onChange={ () => {setIsAgreementChecked(!isAgreementChecked) } }
            />

            <LoaderButton
              className={ 'mt-3' }
              block={ true }
              type="submit"
              color="primary"
              disabled={ !isAgreementChecked }
              isLoading={ formik.isSubmitting }
            >
              { 'Pindahkan ke Marketplace' }
            </LoaderButton>
          </Form>
        )
      }
    </Formik>
  )
}
FastSellProductConvertToMarketProductForm.propTypes    = propTypes
FastSellProductConvertToMarketProductForm.defaultProps = defaultProps

export default FastSellProductConvertToMarketProductForm